import { baseUrlEndpoint, baseUrlPattern, resolveBaseUrl } from "../baseUrl";
import {
  MODELS_HELPER_TEXT,
  MODELS_PREFIX,
  openAiModelsFetch,
  withAllowedModelsHint,
} from "../models";
import { openaiUsage } from "../openai/usage";
import { bearerAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import logo from "./logo.svg";

const DEFAULT_MISTRAL_BASE_URL = "https://api.mistral.ai/v1";

const mistralModelsFetch = openAiModelsFetch(
  "Fetch models from Mistral",
  ({ baseUrl }) => baseUrlEndpoint(baseUrl, DEFAULT_MISTRAL_BASE_URL, "/models")
);

export const mistralAi = defineConnector({
  identifier: "mistralAi",
  name: "Mistral AI",
  tagLine: "Open weight AI models",
  logo,
  protocol: "http",
  tags: ["ai", "engineering"],
  defaultUrlPattern: ({ baseUrl }) =>
    baseUrlPattern(baseUrl, DEFAULT_MISTRAL_BASE_URL),
  auth: (app) => bearerAuth(app.secret.apiKey),
  steps: [
    {
      title: "Get an API key",
      items: [
        {
          kind: "markdown",
          content:
            "Visit [La Plateforme](https://console.mistral.ai/api-keys/) and create or copy an API key.",
        },
      ],
    },
    {
      title: "Copy and paste the API key into the field below",
      items: [
        {
          kind: "input",
          label: "Mistral API key",
          required: true,
          target: { kind: "secrets", field: "apiKey" },
        },
        {
          kind: "url",
          label: "Base URL (optional)",
          placeholder: DEFAULT_MISTRAL_BASE_URL,
          defaultValue: DEFAULT_MISTRAL_BASE_URL,
          target: { kind: "configuration", field: "baseUrl" },
          helperText: "Leave blank for the default Mistral API endpoint.",
        },
      ],
    },
    {
      title: "Choose available models (optional)",
      items: [
        {
          kind: "list",
          label: "Available models",
          prefix: MODELS_PREFIX,
          columns: [{ field: "name", placeholder: "mistral-large-latest" }],
          addLabel: "Add model",
          fetch: mistralModelsFetch,
          helperText: MODELS_HELPER_TEXT,
        },
      ],
    },
  ],
  connectionTest: {
    protocol: "http",
    request: ({ baseUrl }) =>
      baseUrlEndpoint(baseUrl, DEFAULT_MISTRAL_BASE_URL, "/models"),
  },
  publicEnvVars: ({ baseUrl }) => ({
    MISTRAL_AI_BASE_URL: resolveBaseUrl(baseUrl, DEFAULT_MISTRAL_BASE_URL),
  }),
  usageHint: (configuration) =>
    withAllowedModelsHint(
      "Use process.env.MISTRAL_AI_BASE_URL as the Mistral API base URL. Pass a placeholder API key; the proxy injects the real key from the connected app.",
      configuration
    ),
  usage: openaiUsage(),
});
